import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Dialog, IconButton, InputBase, Typography } from "@mui/material";
import { keyframes } from "@mui/system";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import useToken from "../../hooks/useToken";
import { useAuthSlice } from "../../redux/slice/authSlice";
import { useCodetablesSlice } from "../../redux/slice/codetableSlice";
import { useNavigation } from "../../hooks/useNavigation";
import { canAccessIsrByEmail } from "../../utils/isrAccess";
import { bottomItems, sideBarTree, SideBarLeafItem } from "./sidebar/MyProSidebar";
import { useModalStyles } from "./useModalStyles";
import { http } from "../../service/http";
import {
  SEARCH_INVOICE_URL,
  SEARCH_ORDER,
  GET_ALL_IVR_URL,
  FETCH_PRODUCTS_INCLUDING_INACTIVE_FOR_PRODUCT_SEARCH_URL,
  SEARCH_PATIENT_LIST_URL,
  FETCH_PRACTICE_LIST_FOR_FILTER,
  GET_MANUFACTURERS_BY_ROLE_FOR_COMMISSION_URL,
} from "../../service/URL";
import { extractInvoiceListPage } from "../../utils/excelSheet";
import { priceFormatter } from "../../utils/Utility";
import { SearchInvoiceInfo, invoiceInfo } from "../../redux/slice/invoice/InvoiceSlice";
import { SearchOrder, OrderInfo } from "../../redux/slice/order/OrderSlice";
import { SearchPatientRequest, PatientInfo } from "../../redux/slice/patient/patientSlice";
import { PracticeInfo } from "../../redux/slice/practiceSlice";
import { SearchIVRListRequest, IvrListDTO } from "../../redux/slice/ivr/createIVRSlice";
import { SearchPrescriptionRequest } from "../../redux/slice/prescription/prescriptionSlice";
import { ProductInfo } from "../../redux/slice/product/productListSlice";
import {
  PRODUCT_STATUS,
  ORDER_AVAILABILITY,
  ORDER_INVOICE_STATUS,
  CAN_ACCESS_APPLICATION,
} from "../../constants/applicationConstants";

export type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
};

type SearchResult = {
  key: string;
  title: string;
  subtitle?: string;
  icon: JSX.Element;
  select: () => void;
};

type Section = { label: string; results: SearchResult[] };

const PAGES_GROUP = "Pages";
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 400;
const MAX_RESULTS_PER_ENTITY = 5;

const SILENT_REQUEST_CONFIG = { headers: { "x-skip-loader": "true" } };
const SILENT_INTERCEPTOR_OPTION = { skipErrorPopup: true, hideLoadingPopup: true };

const searchPulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.55; }
  50% { transform: scale(1.18); opacity: 1; }
`;

const dotBounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
`;

const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [remoteSections, setRemoteSections] = useState<Section[]>([]);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const resultRefs = useRef<Array<HTMLElement | null>>([]);
  const debounceRef = useRef<number | null>(null);
  const searchSeqRef = useRef(0);
  const practiceCacheRef = useRef<PracticeInfo[] | null>(null);
  const productCacheRef = useRef<ProductInfo[] | null>(null);
  const manufacturerCacheRef = useRef<{ manufacturerId: number; manufacturerName: string }[] | null>(null);

  const { navigate } = useNavigation();
  const isTokenValueAvailable = useToken();
  const { userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap } = useCodetablesSlice();
  const canAccessIsr = canAccessIsrByEmail(allCodeTablesValuesMap, userInfo?.userName);
  const { tokens: mTokens, isDark } = useModalStyles();

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setHighlightIdx(0);
    setRemoteSections([]);
    setRemoteLoading(false);
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    setHighlightIdx(0);
    resultRefs.current = [];
  }, [query, remoteSections]);

  useEffect(() => {
    const el = resultRefs.current[highlightIdx];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  const pageResults = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    const rows: SearchResult[] = [];
    const pushLeaf = (leaf: SideBarLeafItem, subtitle?: string) => {
      rows.push({
        key: `page-${leaf.to}`,
        title: leaf.title,
        subtitle,
        icon: leaf.icon,
        select: () => navigate(leaf.to),
      });
    };
    sideBarTree.forEach((node) => {
      if (node.type === "item") {
        const canSee = node.requiresIsrEmailAccess ? canAccessIsr : isTokenValueAvailable(node.token);
        if (canSee && (!q || node.title.toLowerCase().includes(q))) pushLeaf(node);
      } else {
        const groupMatch = !q || node.title.toLowerCase().includes(q);
        node.children.forEach((child) => {
          const canSee = child.requiresIsrEmailAccess ? canAccessIsr : isTokenValueAvailable(child.token);
          if (canSee && (!q || child.title.toLowerCase().includes(q) || groupMatch)) pushLeaf(child, node.title);
        });
      }
    });
    bottomItems.forEach((b) => {
      if (isTokenValueAvailable(b.token) && (!q || b.title.toLowerCase().includes(q))) pushLeaf(b);
    });
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isTokenValueAvailable, canAccessIsr]);

  const buildInvoiceCriteria = (extra: Partial<SearchInvoiceInfo>): SearchInvoiceInfo => ({
    fromDate: "",
    toDate: "",
    agencyIds: [],
    patientIds: [],
    mfgIds: [],
    practiceIds: [],
    invoiceStatus: ["PAID", "PARTIAL", "UNPAID"],
    currentPage: 1,
    itemsPerPage: MAX_RESULTS_PER_ENTITY,
    isDownload: false,
    excludePurchaseAndBill: true,
    ...extra,
  });

  const buildOrderCriteria = (extra: Partial<SearchOrder>): SearchOrder => ({
    practiceIds: [],
    manufacturerIds: [],
    patientFirstName: "",
    patientLastName: "",
    productIds: [],
    providerName: "",
    orderNumber: "",
    invoiceNumber: "",
    serialNumber: "",
    fromDate: "",
    toDate: "",
    status: Object.values(PRODUCT_STATUS),
    invoiceStatus: Object.values(ORDER_INVOICE_STATUS),
    currentPage: 1,
    itemsPerPage: MAX_RESULTS_PER_ENTITY,
    downloadState: false,
    orderItemStatus: Object.values(ORDER_AVAILABILITY),
    fromServiceDate: "",
    toServiceDate: "",
    withoutIvr: "",
    alertOrders: false,
    ...extra,
  });

  // The two invoice-based lists share one criteria object, so one helper builds both groups.
  const invoiceListSections = (
    rows: { key: string; title: string; subtitle?: string; criteria: SearchInvoiceInfo }[]
  ): Section[] => {
    const sections: Section[] = [];
    const make = (label: string, path: string, allowed: boolean) => {
      if (!allowed || rows.length === 0) return;
      sections.push({
        label,
        results: rows.map((r) => ({
          key: `${path}-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            navigate(path, { state: { invoiceListSearchCriteria: { ...r.criteria, currentPage: 1, itemsPerPage: 10 } } }),
        })),
      });
    };
    make("Invoice List", "/invoice_list", !!isTokenValueAvailable(CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT));
    make("Finance HQ List", "/commission_list", !!isTokenValueAvailable(CAN_ACCESS_APPLICATION.COMMISSION_MANAGEMENT));
    return sections;
  };

  const orderListSection = (
    rows: { key: string; title: string; subtitle?: string; criteria: SearchOrder }[]
  ): Section[] => {
    if (rows.length === 0 || !isTokenValueAvailable(CAN_ACCESS_APPLICATION.ORDER_MANAGEMENT)) return [];
    return [
      {
        label: "Order List",
        results: rows.map((r) => ({
          key: `order-list-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            navigate("/order_list", { state: { orderListSearchCriteria: { ...r.criteria, currentPage: 1, itemsPerPage: 10 } } }),
        })),
      },
    ];
  };

  // Matches a typed date such as 9/24/2026 or 09/24/2026 and normalises it to MM/DD/YYYY.
  const parseDateQuery = (q: string): string | null => {
    const m = q.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!m) return null;
    return `${m[1].padStart(2, "0")}/${m[2].padStart(2, "0")}/${m[3]}`;
  };

  const invoiceSubtitle = (inv: invoiceInfo) =>
    [inv.practiceName, priceFormatter(Number(inv.invoiceAmount) || 0), inv.invoiceStatus].filter(Boolean).join(" · ");

  // separately for the patient-list search criteria.
  const splitPatientName = (fullName: string | undefined): { firstName: string; lastName: string } => {
    const parts = (fullName || "").trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { firstName: "", lastName: "" };
    if (parts.length === 1) return { firstName: parts[0], lastName: "" };
    return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
  };

  // an order or invoice search, so the matching patient surfaces too.
  const patientSectionsFor = (
    patients: { patientId: number; patientName: string; subtitle: string }[]
  ): Section[] => {
    const byId = new Map<number, { patientName: string; subtitle: string }>();
    patients.forEach((p) => {
      if (!Number.isFinite(p.patientId) || p.patientId <= 0 || byId.has(p.patientId)) return;
      byId.set(p.patientId, { patientName: p.patientName, subtitle: p.subtitle });
    });
    if (byId.size === 0) return [];
    const entries = Array.from(byId.entries());
    const sections: Section[] = [];
    if (isTokenValueAvailable(CAN_ACCESS_APPLICATION.PATIENT)) {
      sections.push({
        label: "Patient List",
        results: entries.map(([patientId, p]) => {
          const { firstName, lastName } = splitPatientName(p.patientName);
          return {
            key: `related-patient-list-${patientId}`,
            title: p.patientName || "Unnamed patient",
            subtitle: p.subtitle,
            icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />,
            select: () =>
              navigate("/patients", {
                state: {
                  patientListSearchCriteria: {
                    firstName,
                    lastName,
                    isSurgicalPracticesAvailable: false,
                    patientStatus: [],
                    practiceIds: [],
                    currentPage: 1,
                    itemsPerPage: 20,
                  } as SearchPatientRequest,
                },
              }),
          };
        }),
      });
    }
    sections.push({
      label: "Patient Details",
      results: entries.map(([patientId, p]) => ({
        key: `related-patient-detail-${patientId}`,
        title: p.patientName || "Unnamed patient",
        subtitle: p.subtitle,
        icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />,
        select: () =>
          navigate("/patientDetails", {
            state: { patientDetails: { patientId, patientName: p.patientName } },
          }),
      })),
    });
    return sections;
  };

  // Builds the "Practice List" / "Practice Details" groups for practices found via
  // another entity search (e.g. an IVR), so the matching practice surfaces too.
  const practiceSectionsFor = (
    practices: { practiceId: number; practiceName: string; subtitle: string }[]
  ): Section[] => {
    const byId = new Map<number, { practiceName: string; subtitle: string }>();
    practices.forEach((p) => {
      if (!Number.isFinite(p.practiceId) || p.practiceId <= 0 || byId.has(p.practiceId)) return;
      byId.set(p.practiceId, { practiceName: p.practiceName, subtitle: p.subtitle });
    });
    if (byId.size === 0) return [];
    const entries = Array.from(byId.entries());
    const sections: Section[] = [];
    if (isTokenValueAvailable(CAN_ACCESS_APPLICATION.PRACTICE)) {
      sections.push({
        label: "Practice List",
        results: entries.map(([practiceId, p]) => ({
          key: `related-practice-list-${practiceId}`,
          title: p.practiceName || "Unnamed practice",
          subtitle: p.subtitle,
          icon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            navigate("/practice_list", {
              state: {
                practiceListSearchCriteria: {
                  practiceId,
                  contactFirstName: "",
                  contactLastName: "",
                  contactEmail: "",
                  contactPhone: "",
                  approvalStatus: "",
                  agencyId: null,
                  userId: null,
                  currentPage: 1,
                  itemsPerPage: 10,
                },
              },
            }),
        })),
      });
      sections.push({
        label: "Practice Details",
        results: entries.map(([practiceId, p]) => ({
          key: `related-practice-detail-${practiceId}`,
          title: p.practiceName || "Unnamed practice",
          subtitle: p.subtitle,
          icon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/practiceDetails", { state: { practiceData: { practiceId, practiceName: p.practiceName } } }),
        })),
      });
    }
    return sections;
  };

  const fetchInvoiceRecords = async (criteria: SearchInvoiceInfo): Promise<invoiceInfo[]> => {
    const response = await http.post(SEARCH_INVOICE_URL, criteria, SILENT_REQUEST_CONFIG, SILENT_INTERCEPTOR_OPTION);
    return (extractInvoiceListPage(response).records as invoiceInfo[]).slice(0, MAX_RESULTS_PER_ENTITY);
  };

  const fetchOrderRecords = async (criteria: SearchOrder): Promise<OrderInfo[]> => {
    const response = await http.post(SEARCH_ORDER, criteria, SILENT_REQUEST_CONFIG, SILENT_INTERCEPTOR_OPTION);
    return ((response?.data?.data?.content || []) as OrderInfo[]).slice(0, MAX_RESULTS_PER_ENTITY);
  };

  const buildIvrCriteria = (extra: Partial<SearchIVRListRequest>): SearchIVRListRequest => ({
    productId: null,
    productCategory: null,
    practiceId: null,
    patientFirstName: "",
    patientLastName: "",
    verificationStatus: [],
    ivrNumber: "",
    fromDate: "",
    toDate: "",
    currentPage: 1,
    itemsPerPage: MAX_RESULTS_PER_ENTITY,
    ...extra,
  });

  const fetchIvrRecords = async (criteria: SearchIVRListRequest): Promise<IvrListDTO[]> => {
    const response = await http.post(GET_ALL_IVR_URL, criteria, SILENT_REQUEST_CONFIG, SILENT_INTERCEPTOR_OPTION);
    return ((response?.data?.data?.searchIvrData?.content || []) as IvrListDTO[]).slice(0, MAX_RESULTS_PER_ENTITY);
  };

  // The practice, patient and Rx requests tied to a set of IVRs — shared by any
  // search (IVR number, product name, ...) that ends up with IVR records in hand.
  const relatedSectionsFromIvrs = (records: IvrListDTO[], includeProduct: boolean): Section[] => {
    const sections: Section[] = [];
    sections.push(
      ...practiceSectionsFor(
        records.map((ivr) => ({
          practiceId: Number(ivr.practiceId),
          practiceName: ivr.practiceName,
          subtitle: `Practice on IVR ${ivr.ivrNumber || ivr.ivrId}`,
        }))
      ),
      ...patientSectionsFor(
        records.map((ivr) => ({
          patientId: Number(ivr.patientId),
          patientName: [ivr.patientFirstName, ivr.patientLastName].filter(Boolean).join(" "),
          subtitle: `Patient on IVR ${ivr.ivrNumber || ivr.ivrId}`,
        }))
      )
    );
    if (includeProduct) {
      const productMap = new Map<number, string>();
      records.forEach((ivr) => {
        const productId = Number(ivr.productId);
        if (Number.isFinite(productId) && productId > 0 && !productMap.has(productId)) {
          productMap.set(productId, ivr.productName);
        }
      });
      if (productMap.size > 0) {
        sections.push({
          label: "Product Details",
          results: Array.from(productMap.entries()).map(([productId, productName]) => ({
            key: `related-product-detail-${productId}`,
            title: productName || "Unnamed product",
            subtitle: "Product on this IVR",
            icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
            select: () => navigate("/product?id=" + productId),
          })),
        });
      }
    }
    const rxRowsSeen = new Set<string>();
    const rxRows = records.filter((ivr) => {
      if (!Number.isFinite(Number(ivr.practiceId)) || Number(ivr.practiceId) <= 0) return false;
      const dedupeKey = `${ivr.practiceId}|${ivr.patientFirstName || ""}|${ivr.patientLastName || ""}`;
      if (rxRowsSeen.has(dedupeKey)) return false;
      rxRowsSeen.add(dedupeKey);
      return true;
    });
    if (rxRows.length > 0 && isTokenValueAvailable(CAN_ACCESS_APPLICATION.PRESCRIPTION_MANAGEMENT)) {
      sections.push({
        label: "Rx Request List",
        results: rxRows.map((ivr) => ({
          key: `related-rx-list-${ivr.ivrId}`,
          title: [ivr.patientFirstName, ivr.patientLastName].filter(Boolean).join(" ") || ivr.practiceName,
          subtitle: `Rx requests for ${ivr.practiceName}`,
          icon: <AssignmentOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            navigate("/prescription_list", {
              state: {
                prescriptionListSearchCriteria: {
                  practiceIds: [Number(ivr.practiceId)],
                  patientFirstName: ivr.patientFirstName || "",
                  patientLastName: ivr.patientLastName || "",
                  providerIds: [],
                  prescriptionNumber: "",
                  fromDate: "",
                  toDate: "",
                  status: [],
                  manufacturerId: null,
                  currentPage: 1,
                  itemsPerPage: 10,
                } as SearchPrescriptionRequest,
              },
            }),
        })),
      });
    }
    return sections;
  };

  // An invoice can be found by its own number, its order number, or its invoice date,
  // and shows up in both invoice-based lists plus its detail page.
  const searchInvoices = async (q: string): Promise<Section[]> => {
    const dateQuery = parseDateQuery(q);
    const probes: { name: string; criteria: SearchInvoiceInfo }[] = [
      { name: "inv", criteria: buildInvoiceCriteria({ mfgInvoiceNumber: q }) },
      { name: "ord", criteria: buildInvoiceCriteria({ orderNumber: q }) },
    ];
    if (dateQuery) probes.push({ name: "date", criteria: buildInvoiceCriteria({ fromDate: dateQuery, toDate: dateQuery }) });
    const results = await Promise.all(
      probes.map((p) => fetchInvoiceRecords(p.criteria).then((records) => ({ ...p, records })).catch(() => ({ ...p, records: [] as invoiceInfo[] })))
    );
    const seen = new Set<number>();
    const rows: { key: string; title: string; subtitle?: string; criteria: SearchInvoiceInfo; inv: invoiceInfo }[] = [];
    results.forEach((r) =>
      r.records.forEach((inv) => {
        if (seen.has(inv.invoiceId)) return;
        seen.add(inv.invoiceId);
        rows.push({
          key: `${r.name}-${inv.invoiceId}`,
          title: String(inv.mfgInvoiceNumber ?? inv.invoiceId),
          subtitle: invoiceSubtitle(inv),
          criteria: r.criteria,
          inv,
        });
      })
    );
    const sections = invoiceListSections(rows);
    if (rows.length > 0) {
      sections.push({
        label: "Invoice Details",
        results: rows.map((r) => ({
          key: `invoice-detail-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/billing_detail", { state: { invoiceId: r.inv.invoiceId } }),
        })),
      });
    }
    if (rows.length > 0) {
      const orderLookups = await Promise.all(
        rows.map((r) => {
          const orderNumber = String(r.inv.raworderNumber || r.inv.orderNumber || "").trim();
          if (!orderNumber) return Promise.resolve([] as OrderInfo[]);
          return fetchOrderRecords(buildOrderCriteria({ orderNumber })).catch(() => [] as OrderInfo[]);
        })
      );
      sections.push(
        ...patientSectionsFor(
          orderLookups.flat().map((ord) => ({
            patientId: Number(ord.patientId),
            patientName: ord.patientName,
            subtitle: `Patient on order ${ord.orderNumber}`,
          }))
        )
      );
    }
    return sections;
  };

  // An order can be found by order number, its invoice number, the provider name, or its order date.
  const searchOrders = async (q: string): Promise<Section[]> => {
    const dateQuery = parseDateQuery(q);
    const probes: { name: string; criteria: SearchOrder }[] = [
      { name: "num", criteria: buildOrderCriteria({ orderNumber: q }) },
      { name: "inv", criteria: buildOrderCriteria({ invoiceNumber: q }) },
      { name: "prov", criteria: buildOrderCriteria({ providerName: q }) },
    ];
    if (dateQuery) probes.push({ name: "date", criteria: buildOrderCriteria({ fromDate: dateQuery, toDate: dateQuery }) });
    const results = await Promise.all(
      probes.map((p) => fetchOrderRecords(p.criteria).then((records) => ({ ...p, records })).catch(() => ({ ...p, records: [] as OrderInfo[] })))
    );
    const seen = new Set<number>();
    const rows: { key: string; title: string; subtitle?: string; criteria: SearchOrder; ord: OrderInfo }[] = [];
    results.forEach((r) =>
      r.records.forEach((ord) => {
        if (seen.has(ord.orderId)) return;
        seen.add(ord.orderId);
        rows.push({
          key: `${r.name}-${ord.orderId}`,
          title: ord.orderNumber,
          subtitle: [ord.patientName || ord.practiceName, ord.orderStatus].filter(Boolean).join(" · "),
          criteria: r.criteria,
          ord,
        });
      })
    );
    const sections = orderListSection(rows);
    if (rows.length > 0) {
      sections.push({
        label: "Order Details",
        results: rows.map((r) => ({
          key: `order-detail-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            r.ord.orderNumber?.startsWith("M")
              ? navigate("/migrate_details/1", { state: { orderDetails: r.ord } })
              : navigate("/details/1", { state: { orderDetails: r.ord } }),
        })),
      });
    }
    // The patient on each matched order, so searching by order number also surfaces them.
    sections.push(
      ...patientSectionsFor(
        rows.map((r) => ({
          patientId: Number(r.ord.patientId),
          patientName: r.ord.patientName,
          subtitle: `Patient on order ${r.ord.orderNumber}`,
        }))
      )
    );
    return sections;
  };

  const searchPatients = async (q: string): Promise<Section[]> => {
    const basePayload = {
      isSurgicalPracticesAvailable: false,
      patientStatus: [],
      practiceIds: [],
      currentPage: 1,
      itemsPerPage: MAX_RESULTS_PER_ENTITY,
    };
    const words = q.trim().split(/\s+/).filter(Boolean);
    // A single word could be a first name or a last name, so probe both. Two or
    // more words could be "First Last" or a multi-word first/last name, so try
    // splitting after the first word and before the last word.
    const probes: SearchPatientRequest[] =
      words.length <= 1
        ? [
            { ...basePayload, firstName: q, lastName: "" },
            { ...basePayload, firstName: "", lastName: q },
          ]
        : [
            { ...basePayload, firstName: words[0], lastName: words.slice(1).join(" ") },
            { ...basePayload, firstName: words.slice(0, -1).join(" "), lastName: words[words.length - 1] },
          ];
    const probeResults = await Promise.all(
      probes.map((payload) =>
        http
          .post(SEARCH_PATIENT_LIST_URL, payload, SILENT_REQUEST_CONFIG, SILENT_INTERCEPTOR_OPTION)
          .then((response) => (response?.data?.data?.searchPatientData?.content || []) as PatientInfo[])
          .catch(() => [] as PatientInfo[])
      )
    );
    const seen = new Set<string | number>();
    const records: PatientInfo[] = [];
    probeResults.flat().forEach((p) => {
      if (seen.has(p.patientId)) return;
      seen.add(p.patientId);
      if (records.length < MAX_RESULTS_PER_ENTITY) records.push(p);
    });
    const payload = { ...basePayload, firstName: q, lastName: "" } as SearchPatientRequest;
    const fullNameOf = (p: PatientInfo) =>
      [p.firstName, p.lastName].filter(Boolean).join(" ") || "Unnamed patient";
    const toRow = (p: PatientInfo, prefix: string, select: () => void): SearchResult => ({
      key: `${prefix}-${p.patientId}`,
      title: fullNameOf(p),
      subtitle: p.practiceName || undefined,
      icon: <PersonOutlineIcon sx={{ fontSize: 18 }} />,
      select,
    });
    
    const [patientOrderResults, patientInvoiceResults] = await Promise.all([
      Promise.all(
        records.map((p) =>
          fetchOrderRecords(
            buildOrderCriteria({ patientFirstName: p.firstName || q, patientLastName: p.lastName || "" })
          ).catch(() => [] as OrderInfo[])
        )
      ),
      Promise.all(
        records.map((p) =>
          fetchInvoiceRecords(buildInvoiceCriteria({ patientIds: [Number(p.patientId)] })).catch(
            () => [] as invoiceInfo[]
          )
        )
      ),
    ]);
    const orderSeen = new Set<number>();
    const patientOrders: OrderInfo[] = [];
    patientOrderResults.flat().forEach((ord) => {
      if (orderSeen.has(ord.orderId)) return;
      orderSeen.add(ord.orderId);
      if (patientOrders.length < MAX_RESULTS_PER_ENTITY) patientOrders.push(ord);
    });
    const invoiceSeen = new Set<number>();
    const patientInvoices: invoiceInfo[] = [];
    patientInvoiceResults.flat().forEach((inv) => {
      if (invoiceSeen.has(inv.invoiceId)) return;
      invoiceSeen.add(inv.invoiceId);
      if (patientInvoices.length < MAX_RESULTS_PER_ENTITY) patientInvoices.push(inv);
    });

    const sections: Section[] = [];
    if (isTokenValueAvailable(CAN_ACCESS_APPLICATION.PATIENT)) {
      sections.push({
        label: "Patient List",
        results: records.map((p) =>
          toRow(p, "patient-list", () =>
            navigate("/patients", {
              state: {
                patientListSearchCriteria: {
                  ...payload,
                  firstName: p.firstName || q,
                  lastName: p.lastName || "",
                  itemsPerPage: 20,
                } as SearchPatientRequest,
              },
            })
          )
        ),
      });
    }
    // Orders: filtered list, then this patient's actual orders.
    sections.push(
      ...orderListSection(
        records.map((p) => ({
          key: `patient-${p.patientId}`,
          title: fullNameOf(p),
          subtitle: "Orders for this patient",
          criteria: buildOrderCriteria({ patientFirstName: p.firstName || q, patientLastName: p.lastName || "" }),
        }))
      )
    );
    if (patientOrders.length > 0) {
      sections.push({
        label: "Order Details",
        results: patientOrders.map((ord) => ({
          key: `patient-order-detail-${ord.orderId}`,
          title: ord.orderNumber,
          subtitle: [ord.patientName || ord.practiceName, ord.orderStatus].filter(Boolean).join(" · "),
          icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            ord.orderNumber?.startsWith("M")
              ? navigate("/migrate_details/1", { state: { orderDetails: ord } })
              : navigate("/details/1", { state: { orderDetails: ord } }),
        })),
      });
    }
    // Invoices: filtered lists, then this patient's actual invoices.
    sections.push(
      ...invoiceListSections(
        records.map((p) => ({
          key: `patient-${p.patientId}`,
          title: fullNameOf(p),
          subtitle: "Invoices for this patient",
          criteria: buildInvoiceCriteria({ patientIds: [Number(p.patientId)] }),
        }))
      )
    );
    if (patientInvoices.length > 0) {
      sections.push({
        label: "Invoice Details",
        results: patientInvoices.map((inv) => ({
          key: `patient-invoice-detail-${inv.invoiceId}`,
          title: String(inv.mfgInvoiceNumber ?? inv.invoiceId),
          subtitle: invoiceSubtitle(inv),
          icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/billing_detail", { state: { invoiceId: inv.invoiceId } }),
        })),
      });
    }
    sections.push({
      label: "Patient Details",
      results: records.map((p) =>
        toRow(p, "patient-detail", () =>
          navigate("/patientDetails", {
            state: { patientDetails: { patientId: p.patientId, patientName: fullNameOf(p), dob: p.birthDate } },
          })
        )
      ),
    });
    return sections;
  };

  const searchPractices = async (q: string): Promise<Section[]> => {
    if (!practiceCacheRef.current) {
      const response = await http.get(FETCH_PRACTICE_LIST_FOR_FILTER, SILENT_REQUEST_CONFIG, SILENT_INTERCEPTOR_OPTION);
      practiceCacheRef.current = (response?.data?.data as PracticeInfo[]) || [];
    }
    const lower = q.toLowerCase();
    const records = practiceCacheRef.current
      .filter((p) => p.practiceName?.toLowerCase().includes(lower))
      .slice(0, MAX_RESULTS_PER_ENTITY);
    const toRow = (p: PracticeInfo, prefix: string, select: () => void): SearchResult => ({
      key: `${prefix}-${p.practiceId}`,
      title: p.practiceName,
      subtitle: p.approvalStatus || undefined,
      icon: <ApartmentOutlinedIcon sx={{ fontSize: 18 }} />,
      select,
    });
    const sections: Section[] = [];
    if (isTokenValueAvailable(CAN_ACCESS_APPLICATION.PRACTICE)) {
      sections.push({
        label: "Practice List",
        results: records.map((p) =>
          toRow(p, "practice-list", () =>
            navigate("/practice_list", {
              state: {
                practiceListSearchCriteria: {
                  practiceId: Number(p.practiceId),
                  contactFirstName: "",
                  contactLastName: "",
                  contactEmail: "",
                  contactPhone: "",
                  approvalStatus: "",
                  agencyId: null,
                  userId: null,
                  currentPage: 1,
                  itemsPerPage: 10,
                },
              },
            })
          )
        ),
      });
    }
    // The practice's own orders and invoices, filtered by practice.
    sections.push(
      ...orderListSection(
        records.map((p) => ({
          key: `practice-${p.practiceId}`,
          title: p.practiceName,
          subtitle: "Orders for this practice",
          criteria: buildOrderCriteria({ practiceIds: [Number(p.practiceId)] }),
        }))
      ),
      ...invoiceListSections(
        records.map((p) => ({
          key: `practice-${p.practiceId}`,
          title: p.practiceName,
          subtitle: "Invoices for this practice",
          criteria: buildInvoiceCriteria({ practiceIds: [Number(p.practiceId)] }),
        }))
      )
    );
    sections.push({
      label: "Practice Details",
      results: records.map((p) =>
        toRow(p, "practice-detail", () => navigate("/practiceDetails", { state: { practiceData: p } }))
      ),
    });
    return sections;
  };

  // Manufacturers are matched by name (cached list) and open the invoice/order lists filtered to them.
  const searchManufacturers = async (q: string): Promise<Section[]> => {
    if (!manufacturerCacheRef.current) {
      const response = await http.get(
        GET_MANUFACTURERS_BY_ROLE_FOR_COMMISSION_URL,
        SILENT_REQUEST_CONFIG,
        SILENT_INTERCEPTOR_OPTION
      );
      manufacturerCacheRef.current = (response?.data?.data as { manufacturerId: number; manufacturerName: string }[]) || [];
    }
    const lower = q.toLowerCase();
    const records = manufacturerCacheRef.current
      .filter((m) => m.manufacturerName?.toLowerCase().includes(lower))
      .slice(0, MAX_RESULTS_PER_ENTITY);
    return [
      ...orderListSection(
        records.map((m) => ({
          key: `mfr-${m.manufacturerId}`,
          title: m.manufacturerName,
          subtitle: "Orders from this manufacturer",
          criteria: buildOrderCriteria({ manufacturerIds: [Number(m.manufacturerId)] }),
        }))
      ),
      ...invoiceListSections(
        records.map((m) => ({
          key: `mfr-${m.manufacturerId}`,
          title: m.manufacturerName,
          subtitle: "Invoices from this manufacturer",
          criteria: buildInvoiceCriteria({ mfgIds: [Number(m.manufacturerId)] }),
        }))
      ),
    ];
  };

  const searchIVRs = async (q: string): Promise<Section[]> => {
    const criteria = buildIvrCriteria({ ivrNumber: q });
    const records = await fetchIvrRecords(criteria);
    const toRow = (ivr: IvrListDTO, prefix: string, select: () => void): SearchResult => ({
      key: `${prefix}-${ivr.ivrId}`,
      title: ivr.ivrNumber || `IVR ${ivr.ivrId}`,
      subtitle: [[ivr.patientFirstName, ivr.patientLastName].filter(Boolean).join(" "), ivr.verificationStatus]
        .filter(Boolean)
        .join(" · "),
      icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />,
      select,
    });
    const sections: Section[] = [];
    if (isTokenValueAvailable(CAN_ACCESS_APPLICATION.IVR_MANAGEMENT)) {
      sections.push({
        label: "IVR List",
        results: records.map((ivr) =>
          toRow(ivr, "ivr-list", () =>
            navigate("/ivr_list", {
              state: { ivrListSearchCriteria: { ...criteria, ivrNumber: ivr.ivrNumber || q, itemsPerPage: 10 } },
            })
          )
        ),
      });
    }
    sections.push({
      label: "IVR Details",
      results: records.map((ivr) =>
        toRow(ivr, "ivr-detail", () => navigate("/ivr_details", { state: { ivrInfo: ivr } }))
      ),
    });
    // The practice, patient, product and Rx requests tied to these IVRs, so
    // searching an IVR number surfaces everything connected to it, not just the IVR.
    sections.push(...relatedSectionsFromIvrs(records, true));
    return sections;
  };

  const searchProducts = async (q: string): Promise<Section[]> => {
    if (!productCacheRef.current) {
      // FETCH_PRODUCTLIST_URL is scoped narrower (e.g. active-only) and was missing
      // real products — use the same "including inactive, for search" endpoint the
      // product dropdowns elsewhere in the app already rely on.
      const response = await http.get(
        `${FETCH_PRODUCTS_INCLUDING_INACTIVE_FOR_PRODUCT_SEARCH_URL}?includeProductPrice=false`,
        SILENT_REQUEST_CONFIG,
        SILENT_INTERCEPTOR_OPTION
      );
      productCacheRef.current = (response?.data?.data as ProductInfo[]) || [];
    }
    const lower = q.toLowerCase();
    const matches = productCacheRef.current
      .filter((p) => p.productName?.toLowerCase().includes(lower))
      .slice(0, MAX_RESULTS_PER_ENTITY);
    const sections: Section[] = [
      {
        label: "Product Details",
        results: matches.map((p) => ({
          key: `product-${p.productId}`,
          title: p.productName,
          subtitle: p.productCategory || p.manufacturerName || undefined,
          icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/product?id=" + p.productId),
        })),
      },
    ];
    const productIds = matches
      .map((p) => Number(p.productId))
      .filter((id) => Number.isFinite(id) && id > 0);
    if (productIds.length === 0) return sections;

    // Everything tied to these products: who ordered/invoiced/IVR'd them, and from
    // the IVRs, in turn, who the practice/patient/Rx requests connected to them are.
    const [orderResults, invoiceResults, ivrResults] = await Promise.all([
      Promise.all(
        productIds.map((id) =>
          fetchOrderRecords(buildOrderCriteria({ productIds: [id] })).catch(() => [] as OrderInfo[])
        )
      ),
      Promise.all(
        productIds.map((id) =>
          fetchInvoiceRecords(buildInvoiceCriteria({ productIds: [id] })).catch(() => [] as invoiceInfo[])
        )
      ),
      Promise.all(
        productIds.map((id) =>
          fetchIvrRecords(buildIvrCriteria({ productId: [id] })).catch(() => [] as IvrListDTO[])
        )
      ),
    ]);

    const orderSeen = new Set<number>();
    const orderRows: { key: string; title: string; subtitle?: string; criteria: SearchOrder; ord: OrderInfo }[] = [];
    orderResults.flat().forEach((ord) => {
      if (orderSeen.has(ord.orderId)) return;
      orderSeen.add(ord.orderId);
      orderRows.push({
        key: `product-order-${ord.orderId}`,
        title: ord.orderNumber,
        subtitle: [ord.patientName || ord.practiceName, ord.orderStatus].filter(Boolean).join(" · "),
        criteria: buildOrderCriteria({ productIds }),
        ord,
      });
    });
    sections.push(...orderListSection(orderRows));
    if (orderRows.length > 0) {
      sections.push({
        label: "Order Details",
        results: orderRows.map((r) => ({
          key: `product-order-detail-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ShoppingCartOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            r.ord.orderNumber?.startsWith("M")
              ? navigate("/migrate_details/1", { state: { orderDetails: r.ord } })
              : navigate("/details/1", { state: { orderDetails: r.ord } }),
        })),
      });
    }

    const invoiceSeen = new Set<number>();
    const invoiceRows: { key: string; title: string; subtitle?: string; criteria: SearchInvoiceInfo; inv: invoiceInfo }[] = [];
    invoiceResults.flat().forEach((inv) => {
      if (invoiceSeen.has(inv.invoiceId)) return;
      invoiceSeen.add(inv.invoiceId);
      invoiceRows.push({
        key: `product-invoice-${inv.invoiceId}`,
        title: String(inv.mfgInvoiceNumber ?? inv.invoiceId),
        subtitle: invoiceSubtitle(inv),
        criteria: buildInvoiceCriteria({ productIds }),
        inv,
      });
    });
    sections.push(...invoiceListSections(invoiceRows));
    if (invoiceRows.length > 0) {
      sections.push({
        label: "Invoice Details",
        results: invoiceRows.map((r) => ({
          key: `product-invoice-detail-${r.key}`,
          title: r.title,
          subtitle: r.subtitle,
          icon: <ReceiptLongOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/billing_detail", { state: { invoiceId: r.inv.invoiceId } }),
        })),
      });
    }

    const ivrSeen = new Set<number>();
    const ivrRows: IvrListDTO[] = [];
    ivrResults.flat().forEach((ivr) => {
      if (ivrSeen.has(ivr.ivrId)) return;
      ivrSeen.add(ivr.ivrId);
      ivrRows.push(ivr);
    });
    if (ivrRows.length > 0 && isTokenValueAvailable(CAN_ACCESS_APPLICATION.IVR_MANAGEMENT)) {
      sections.push({
        label: "IVR List",
        results: ivrRows.map((ivr) => ({
          key: `product-ivr-list-${ivr.ivrId}`,
          title: ivr.ivrNumber || `IVR ${ivr.ivrId}`,
          subtitle: [[ivr.patientFirstName, ivr.patientLastName].filter(Boolean).join(" "), ivr.verificationStatus]
            .filter(Boolean)
            .join(" · "),
          icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () =>
            navigate("/ivr_list", {
              state: { ivrListSearchCriteria: buildIvrCriteria({ productId: [Number(ivr.productId)], itemsPerPage: 10 }) },
            }),
        })),
      });
      sections.push({
        label: "IVR Details",
        results: ivrRows.map((ivr) => ({
          key: `product-ivr-detail-${ivr.ivrId}`,
          title: ivr.ivrNumber || `IVR ${ivr.ivrId}`,
          subtitle: [[ivr.patientFirstName, ivr.patientLastName].filter(Boolean).join(" "), ivr.verificationStatus]
            .filter(Boolean)
            .join(" · "),
          icon: <VerifiedUserOutlinedIcon sx={{ fontSize: 18 }} />,
          select: () => navigate("/ivr_details", { state: { ivrInfo: ivr } }),
        })),
      });
    }
    sections.push(...relatedSectionsFromIvrs(ivrRows, false));
    return sections;
  };

  // Debounced remote entity search — only once the query is at least 2 characters,
  // so we're not hitting the backend on every single keystroke.
  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (q.length < MIN_QUERY_LENGTH) {
      setRemoteSections([]);
      setRemoteLoading(false);
      return;
    }
    setRemoteLoading(true);
    const seq = ++searchSeqRef.current;
    debounceRef.current = window.setTimeout(async () => {
      const groups = await Promise.all([
        searchInvoices(q).catch((): Section[] => []),
        searchOrders(q).catch((): Section[] => []),
        searchPatients(q).catch((): Section[] => []),
        searchPractices(q).catch((): Section[] => []),
        searchIVRs(q).catch((): Section[] => []),
        searchProducts(q).catch((): Section[] => []),
        searchManufacturers(q).catch((): Section[] => []),
      ]);
      if (searchSeqRef.current !== seq) return; // a newer keystroke superseded this search
      // Several searches can feed the same list (e.g. Invoice List), so merge groups by label.
      // Different entity searches can surface the same record under the same group label
      // (e.g. an order and its invoice both resolving to the same patient) — dedupe by key.
      const merged = new Map<string, Section>();
      groups.flat().forEach((sec) => {
        if (sec.results.length === 0) return;
        const existing = merged.get(sec.label);
        if (existing) {
          const seenKeys = new Set(existing.results.map((r) => r.key));
          existing.results = [...existing.results, ...sec.results.filter((r) => !seenKeys.has(r.key))];
        } else {
          merged.set(sec.label, { label: sec.label, results: [...sec.results] });
        }
      });
      const built: Section[] = Array.from(merged.values());
      setRemoteSections(built);
      setRemoteLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const sections = useMemo<Section[]>(() => {
    const list = [...remoteSections];
    if (pageResults.length > 0) list.push({ label: PAGES_GROUP, results: pageResults });
    return list;
  }, [remoteSections, pageResults]);

  const flatResults = useMemo(() => sections.flatMap((s) => s.results), [sections]);

  const handleSelect = (result: SearchResult) => {
    onClose();
    result.select();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (flatResults.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, flatResults.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = flatResults[highlightIdx];
      if (hit) handleSelect(hit);
    }
  };

  const kbdSx = {
    px: 0.7,
    py: 0.1,
    borderRadius: "6px",
    border: `1px solid ${mTokens.border}`,
    fontSize: "0.68rem",
    fontWeight: 700,
    color: mTokens.textSecondary,
    lineHeight: 1.6,
  };

  let rowCursor = -1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      transitionDuration={150}
      PaperProps={{
        sx: {
          position: "fixed",
          top: "12%",
          m: 0,
          borderRadius: "16px",
          border: `1px solid ${mTokens.border}`,
          backgroundColor: mTokens.bg,
          backgroundImage: "none",
          boxShadow: isDark ? "0 24px 60px rgba(0,0,0,0.55)" : "0 24px 60px rgba(15,23,42,0.22)",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2.25,
          py: 1.6,
          borderBottom: `1px solid ${mTokens.border}`,
        }}
      >
        <SearchIcon sx={{ color: mTokens.accent, fontSize: 22, flexShrink: 0 }} />
        <InputBase
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search patients, invoices, orders..."
          fullWidth
          sx={{
            fontSize: "0.98rem",
            color: mTokens.textPrimary,
            "& input::placeholder": { color: mTokens.textSecondary, opacity: 1 },
          }}
        />
        {remoteLoading ? <CircularProgress size={16} sx={{ color: mTokens.accent, flexShrink: 0 }} /> : null}
        <IconButton
          aria-label="Close search"
          size="small"
          onClick={onClose}
          sx={{ flexShrink: 0, color: mTokens.textSecondary }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          maxHeight: 420,
          overflowY: "auto",
          py: 1,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            background: isDark ? "rgba(255,255,255,0.14)" : "#cbd5e1",
            borderRadius: 4,
          },
        }}
      >
        {flatResults.length === 0 ? (
          remoteLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4, gap: 1.25 }}>
              <SearchIcon
                sx={{
                  fontSize: 28,
                  color: mTokens.accent,
                  animation: `${searchPulse} 1.1s ease-in-out infinite`,
                }}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography sx={{ color: mTokens.textSecondary, fontSize: "0.88rem" }}>Searching</Typography>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      backgroundColor: mTokens.accent,
                      animation: `${dotBounce} 1.1s ease-in-out infinite`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Typography sx={{ px: 2.5, py: 3, textAlign: "center", color: mTokens.textSecondary, fontSize: "0.88rem" }}>
              No results found.
            </Typography>
          )
        ) : (
          sections.map((section) => (
            <Box key={section.label} sx={{ mb: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.25,
                  pt: 1,
                  pb: 0.5,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: mTokens.textSecondary,
                  }}
                >
                  {section.label}
                </Typography>
                <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: mTokens.textSecondary }}>
                  {section.results.length} result{section.results.length === 1 ? "" : "s"}
                </Typography>
              </Box>
              {section.results.map((result) => {
                rowCursor += 1;
                const idx = rowCursor;
                const isActive = idx === highlightIdx;
                return (
                  <Box
                    key={result.key}
                    ref={(el: HTMLElement | null) => {
                      resultRefs.current[idx] = el;
                    }}
                    role="button"
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onClick={() => handleSelect(result)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      mx: 1,
                      px: 1.5,
                      py: 1.1,
                      borderRadius: "10px",
                      cursor: "pointer",
                      backgroundColor: isActive
                        ? isDark
                          ? "rgba(20,184,166,0.14)"
                          : "rgba(20,184,166,0.08)"
                        : "transparent",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: "9px",
                        color: mTokens.accent,
                        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
                      }}
                    >
                      {result.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 600, color: mTokens.textPrimary }}>
                        {result.title}
                      </Typography>
                      {result.subtitle ? (
                        <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary }}>
                          {result.subtitle}
                        </Typography>
                      ) : null}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.4,
                        flexShrink: 0,
                        opacity: isActive ? 1 : 0,
                        transition: "opacity 0.15s ease",
                        color: mTokens.accent,
                      }}
                    >
                      <Typography sx={{ fontSize: "0.72rem", fontWeight: 700 }}>Open</Typography>
                      <ArrowForwardIcon sx={{ fontSize: 14 }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2.25,
          py: 1,
          borderTop: `1px solid ${mTokens.border}`,
          backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={kbdSx}>&uarr;&darr;</Box>
          <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary }}>Navigate</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={kbdSx}>Enter</Box>
          <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary }}>Select</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
          <Box sx={kbdSx}>Esc</Box>
          <Typography sx={{ fontSize: "0.72rem", color: mTokens.textSecondary }}>Close</Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
